// transcript.ts — the pure reducers behind the Sessions chat transcript.
//
// Every frame that writes to the open conversation — from the session's
// socket or from the response-streaming fallback — goes through one of
// these, so a turn renders identically however its frames reached the
// page. Each takes the previous message list and returns the next one
// (never mutating it), which is the shape React's functional setState
// wants. `now` is injectable so tests can pin timestamps.
//
//   upsertToolCall        merge a patch into the bubble for one tool call.
//   upsertToolUi          attach a tool's live UI to its bubble.
//   appendToLastAssistant stream a content/reasoning fragment.
//   appendTurnError       land a turn failure in the transcript.
//   openTurnBubbles       open the bubbles for a turn that just began.
//   appendSteer           show a steer message, deduped against an echo.
//   appendUserMessage     show what the user just sent.

import type { SessionMessage } from '../api';

/** Returns the timestamp a new bubble is stamped with. */
export type Clock = () => string;

const isoNow: Clock = () => new Date().toISOString();

/** The index of the bubble recording `toolCallId`, or -1. */
function toolCallIndex(prev: readonly SessionMessage[], toolCallId: string): number {
  return prev.findIndex((m) => m.tool_call_id && m.tool_call_id === toolCallId);
}

// Merge a patch into the message recording one tool call, creating it if
// this is the first thing we've heard about that call. Keyed by
// tool_call_id so a turn running several tools in parallel keeps them
// apart — which is exactly why the frames carry the id.
export function upsertToolCall(
  prev: SessionMessage[],
  toolCallId: string,
  patch: Partial<SessionMessage>,
  now: Clock = isoNow,
): SessionMessage[] {
  const index = toolCallIndex(prev, toolCallId);
  if (index >= 0) {
    const out = prev.slice();
    out[index] = { ...out[index], ...patch };
    return out;
  }
  return [...prev, {
    role: 'assistant',
    content: '',
    tool_call_id: toolCallId,
    timestamp: now(),
    ...patch,
  }];
}

// A tool's UI is its own bubble rather than a field on the assistant turn:
// the turn is still streaming text when the UI lands, and folding it into
// that bubble would make the UI's position depend on how much prose
// happened to arrive first — exactly the kind of moving target that costs
// a live component its mount.
export function upsertToolUi(
  prev: SessionMessage[],
  toolCallId: string,
  markdown: string,
  now: Clock = isoNow,
): SessionMessage[] {
  const index = toolCallIndex(prev, toolCallId);
  if (index >= 0) {
    const out = prev.slice();
    out[index] = { ...out[index], tool_ui: markdown };
    return out;
  }
  return [...prev, {
    role: 'assistant',
    content: '',
    tool_ui: markdown,
    tool_call_id: toolCallId,
    timestamp: now(),
  }];
}

// Append a streamed fragment to the tail assistant bubble.
//
// A recoverable error mid-turn leaves an error bubble as the tail. Text
// that arrives after it is the model still talking, so it starts a fresh
// bubble instead of being appended into — and silently restyled as part
// of — the failure notice.
export function appendToLastAssistant(
  prev: SessionMessage[],
  field: 'content' | 'reasoning',
  fragment: string,
  now: Clock = isoNow,
): SessionMessage[] {
  const out = prev.slice();
  const last = out[out.length - 1];
  if (last && last.role === 'assistant' && !last.error) {
    out[out.length - 1] = { ...last, [field]: (last[field] ?? '') + fragment };
    return out;
  }
  const fresh: SessionMessage = {
    role: 'assistant',
    content: '',
    reasoning: '',
    timestamp: now(),
  };
  fresh[field] = fragment;
  out.push(fresh);
  return out;
}

// Turn failures land in the transcript, not in a banner: that's where the
// failure happened, it's what the Pod persists, and it's what a reload
// will show.
//
// If nothing had streamed into the placeholder yet (an upstream 400 on
// the first token, say) it is replaced in place, so a failed turn doesn't
// strand a "…" ghost bubble above the error. A partially-streamed reply
// is kept and the error appended after it.
export function appendTurnError(
  prev: SessionMessage[],
  message: string,
  now: Clock = isoNow,
): SessionMessage[] {
  const failure: SessionMessage = {
    role: 'assistant',
    content: message,
    error: true,
    timestamp: now(),
  };
  const last = prev[prev.length - 1];
  const ghost = last && last.role === 'assistant' && !last.error
    && !last.content && !last.reasoning;
  return ghost ? [...prev.slice(0, -1), failure] : [...prev, failure];
}

// Open a bubble for a turn that has just begun.
//
// `prompt` is echoed by the harness on turn_start, which is how a turn
// started somewhere else — another tab, the CLI — gets the user's message
// rendered here at all. It is deduped rather than always appended because
// this tab shows its own message optimistically the moment it is sent,
// and the frame confirming it must not double it.
export function openTurnBubbles(
  prev: SessionMessage[],
  prompt: string,
  now: Clock = isoNow,
): SessionMessage[] {
  const last = prev[prev.length - 1];
  const alreadyShown = !!last && last.role === 'user' && last.content === prompt;
  const out: SessionMessage[] = alreadyShown || !prompt
    ? prev.slice()
    : [...prev, { role: 'user', content: prompt, timestamp: now() }];
  out.push({
    role: 'assistant',
    content: '',
    reasoning: '',
    timestamp: now(),
  });
  return out;
}

// A steer frame echoes a message this tab may already be showing. Returns
// `prev` itself when it is, so React bails out of the re-render.
export function appendSteer(
  prev: SessionMessage[],
  message: string,
  now: Clock = isoNow,
): SessionMessage[] {
  const last = prev[prev.length - 1];
  if (last && last.role === 'user' && last.content === message) return prev;
  return [...prev, { role: 'user', content: message, timestamp: now() }];
}

// Show the user's own message at once; the turn_start frame that follows
// confirms it rather than repeating it (see openTurnBubbles).
export function appendUserMessage(
  prev: SessionMessage[],
  content: string,
  now: Clock = isoNow,
): SessionMessage[] {
  return [...prev, { role: 'user', content, timestamp: now() }];
}
